// Static reference data: every external API the Conexus Foundation platform integrates with.
// Updated 2026-06. Keep in sync with backend/server.py KNOWN_API_SERVICES and Flutterwave/SMTP seeders.

export const API_REFERENCE = [
  // ─── KEY-REQUIRED APIs (managed in Admin → API Manager) ─────────────────────
  {
    section: "NASA / Earth Observation",
    id: "nasa",
    name: "NASA Open APIs",
    purpose: "DONKI (solar flares, CMEs, geomagnetic storms), NeoWs (near-Earth asteroids), APOD, GIBS imagery metadata.",
    used_in: ["/api/space/solar-activity", "/api/space/neo", "/api/v1/space/*", "Ticker alerts"],
    test_endpoint: "https://api.nasa.gov/planetary/apod?api_key=DEMO_KEY",
    live_endpoint: "https://api.nasa.gov/planetary/apod?api_key=YOUR_KEY",
    requires_key: true,
    free_tier: "1,000 req/hr with key (30/hr without).",
    signup: "https://api.nasa.gov/",
    how_to_insert: "Admin → API Manager → \"NASA API Key\" → paste key → Save → Test.",
    storage: "system_api_keys collection (_id = nasa).",
  },
  {
    section: "NASA / Earth Observation",
    id: "earthdata",
    name: "NASA Earthdata (AppEEARS + CMR)",
    purpose: "Pixel-level MODIS NDVI extraction (MOD13Q1), CMR granule search for satellite scenes.",
    used_in: ["/api/v1/ndvi/pixel", "/api/v1/ndvi/pixel/task/{id}", "/api/satellite/analysis"],
    test_endpoint: "https://cmr.earthdata.nasa.gov/search/collections.json?limit=1",
    live_endpoint: "https://appeears.earthdatacloud.nasa.gov/api/task",
    requires_key: true,
    free_tier: "Free with NASA Earthdata Login account.",
    signup: "https://urs.earthdata.nasa.gov/profile (register, then generate a bearer token).",
    how_to_insert: "Admin → API Manager → \"NASA Earthdata Token\" → paste bearer token → Save → Test.",
    storage: "system_api_keys collection (_id = earthdata).",
  },

  // ─── PAYMENTS ───────────────────────────────────────────────────────────────
  {
    section: "Payments",
    id: "flutterwave_test",
    name: "Flutterwave (Test)",
    purpose: "Sandbox card / mobile-money / bank checkout for donations, Carbon Hub purchases, and Developer API subscriptions.",
    used_in: ["/api/payments/donations/init", "/api/payments/hub/init/{project_id}", "/api/payments/subscribe/init/{plan_id}", "/api/payments/webhook"],
    test_endpoint: "https://api.flutterwave.com/v3 (test keys begin with FLWPUBK_TEST- / FLWSECK_TEST-).",
    live_endpoint: "https://api.flutterwave.com/v3 (live keys begin with FLWPUBK- / FLWSECK-).",
    requires_key: true,
    free_tier: "Test mode is free, unlimited transactions. Use sandbox cards (e.g. 4187 4274 1556 4246).",
    signup: "https://dashboard.flutterwave.com/dashboard/settings/apis (Test tab).",
    how_to_insert: "Admin → Payments → Mode: \"Test\" → Auto-seeded test keys are pre-filled. To override, paste your own test keys.",
    storage: "system_api_keys collection (_id = flutterwave_test) with public_key, secret_key, encryption_key, webhook_secret_hash.",
  },
  {
    section: "Payments",
    id: "flutterwave_live",
    name: "Flutterwave (Live / Production)",
    purpose: "Live card / mobile-money / bank checkout. Real money flows to your Flutterwave account.",
    used_in: ["Same endpoints as test mode when Admin → Payments → Mode = Live."],
    test_endpoint: "n/a — live keys only.",
    live_endpoint: "https://api.flutterwave.com/v3",
    requires_key: true,
    free_tier: "Transaction fees per Flutterwave pricing; no platform-side fee.",
    signup: "https://dashboard.flutterwave.com/dashboard/settings/apis (Live tab). KYB/KYC must be completed.",
    how_to_insert: "Admin → Payments → Mode toggle to \"Live\" → paste FLWPUBK-, FLWSECK-, encryption key, and webhook_secret_hash → Save. Switching mode to Live activates real charges.",
    storage: "system_api_keys collection (_id = flutterwave_live).",
  },

  // ─── EMAIL ──────────────────────────────────────────────────────────────────
  {
    section: "Transactional Email",
    id: "smtp",
    name: "SMTP (Gmail / SendGrid / Mailgun / any provider)",
    purpose: "Sends welcome emails, password-reset OTP, donation receipts, certificate delivery, threshold alerts.",
    used_in: ["/api/auth/register", "/api/auth/forgot-password", "/api/auth/reset-password", "Certificate issuance", "Notification dispatcher"],
    test_endpoint: "smtp.gmail.com:587 (TLS) with a personal Gmail App Password works for verification.",
    live_endpoint: "Any production SMTP relay (SendGrid relay smtp.sendgrid.net:587, Mailgun, AWS SES, etc.).",
    requires_key: true,
    free_tier: "Gmail App Passwords: 500 emails/day free. SendGrid free tier: 100/day.",
    signup: "Gmail: https://myaccount.google.com/apppasswords. SendGrid: https://app.sendgrid.com/settings/api_keys (use API key as SMTP password with username \"apikey\").",
    how_to_insert: "Admin → Payments → \"SMTP Email Service\" panel → fill host (smtp.gmail.com), port (587), TLS=Yes, username, password, from-address, from-name → Save → Send Test.",
    storage: "system_api_keys collection (_id = smtp) with host, port, username, password, use_tls, from_email, from_name.",
  },
  {
    section: "Transactional Email",
    id: "resend",
    name: "Resend (optional alternative)",
    purpose: "Modern HTTP email API. Optional alternative if SMTP path is undesired.",
    used_in: ["Not wired by default — code path exists; activate by enabling Resend in send_email helper."],
    test_endpoint: "https://api.resend.com/domains",
    live_endpoint: "https://api.resend.com/emails",
    requires_key: true,
    free_tier: "3,000 emails/month free.",
    signup: "https://resend.com/api-keys",
    how_to_insert: "Admin → API Manager → \"Resend API Key\" → paste re_xxx key → Save → Test.",
    storage: "system_api_keys collection (_id = resend).",
  },
  {
    section: "Transactional Email",
    id: "sendgrid",
    name: "SendGrid (optional alternative)",
    purpose: "HTTP email API. Use either SendGrid HTTP API or its SMTP relay (smtp.sendgrid.net).",
    used_in: ["Not wired by default — alternative to Resend / SMTP."],
    test_endpoint: "https://api.sendgrid.com/v3/scopes",
    live_endpoint: "https://api.sendgrid.com/v3/mail/send",
    requires_key: true,
    free_tier: "100 emails/day free.",
    signup: "https://app.sendgrid.com/settings/api_keys",
    how_to_insert: "Admin → API Manager → \"SendGrid API Key\" → paste SG.xxx key → Save → Test.",
    storage: "system_api_keys collection (_id = sendgrid).",
  },

  // ─── WEATHER / MAPS ─────────────────────────────────────────────────────────
  {
    section: "Weather & Maps (optional)",
    id: "openweather",
    name: "OpenWeatherMap",
    purpose: "Premium current-weather / forecast / one-call API (optional upgrade over free Open-Meteo).",
    used_in: ["Not wired by default — Open-Meteo is used for weather. Wire by reading get_api_key('openweather') in weather endpoint."],
    test_endpoint: "https://api.openweathermap.org/data/2.5/weather?q=London&appid=YOUR_KEY",
    live_endpoint: "Same endpoint with paid plan key.",
    requires_key: true,
    free_tier: "1,000 calls/day free (current weather + 5-day forecast).",
    signup: "https://openweathermap.org/api",
    how_to_insert: "Admin → API Manager → \"OpenWeatherMap Key\" → paste key → Save → Test.",
    storage: "system_api_keys collection (_id = openweather).",
  },
  {
    section: "Weather & Maps (optional)",
    id: "mapbox",
    name: "Mapbox",
    purpose: "Premium vector basemap tiles (optional upgrade over OpenStreetMap and CartoDB Dark Matter).",
    used_in: ["Not wired by default — Leaflet uses CartoDB and NASA GIBS tiles. Wire by replacing the TileLayer url."],
    test_endpoint: "https://api.mapbox.com/styles/v1?access_token=YOUR_TOKEN",
    live_endpoint: "https://api.mapbox.com/styles/v1/{style}/tiles/{z}/{x}/{y}?access_token=YOUR_TOKEN",
    requires_key: true,
    free_tier: "50,000 map loads/month free.",
    signup: "https://account.mapbox.com/access-tokens/",
    how_to_insert: "Admin → API Manager → \"Mapbox Token\" → paste pk.xxx token → Save → Test.",
    storage: "system_api_keys collection (_id = mapbox).",
  },

  // ─── FREE / NO-KEY APIs ─────────────────────────────────────────────────────
  {
    section: "Free Public APIs (no key required)",
    id: "open-meteo",
    name: "Open-Meteo",
    purpose: "Historical and forecast weather (temperature, precipitation, humidity, wind).",
    used_in: ["/api/weather/grid", "/api/v1/weather", "Data Center temperature & rainfall datasets"],
    test_endpoint: "https://api.open-meteo.com/v1/forecast?latitude=52.5&longitude=13.4&hourly=temperature_2m",
    live_endpoint: "Same — Open-Meteo is free for non-commercial use, no key.",
    requires_key: false,
    free_tier: "10,000 req/day, no API key. Commercial use requires their paid plan.",
    signup: "n/a — public API.",
    how_to_insert: "Nothing to insert. Calls go out directly from backend.",
    storage: "n/a",
  },
  {
    section: "Free Public APIs (no key required)",
    id: "usgs-eq",
    name: "USGS Earthquake Hazards (FDSN / ComCat)",
    purpose: "Real-time + historical earthquake catalog. Used for the earthquake ticker, registry, and Data Center seismic dataset.",
    used_in: ["/api/usgs/earthquakes", "/api/v1/earthquakes", "Data Center earthquake dataset"],
    test_endpoint: "https://earthquake.usgs.gov/fdsnws/event/1/query?format=geojson&limit=1",
    live_endpoint: "Same — USGS FDSN is fully public.",
    requires_key: false,
    free_tier: "Unlimited fair use, no key.",
    signup: "n/a — public API.",
    how_to_insert: "Nothing to insert.",
    storage: "n/a",
  },
  {
    section: "Free Public APIs (no key required)",
    id: "firms",
    name: "NASA FIRMS (active fire detections)",
    purpose: "MODIS + VIIRS thermal anomaly / active-fire pixels. Used for wildfire ticker and Data Center fire dataset.",
    used_in: ["/api/satellite/analysis", "Data Center wildfire dataset", "Ticker fire alerts"],
    test_endpoint: "https://firms.modaps.eosdis.nasa.gov/api/area/csv/[map_key]/MODIS_NRT/world/1",
    live_endpoint: "Same — requires a free MAP_KEY (functionally a low-friction token).",
    requires_key: false,
    free_tier: "5,000 transactions / 10 min with free MAP_KEY. Without a key, public bounding-box reads work.",
    signup: "Optional: https://firms.modaps.eosdis.nasa.gov/api/map_key — paste the MAP_KEY into Earthdata token if used.",
    how_to_insert: "Optional. If you want higher rate limits, request a FIRMS MAP_KEY and store it as a custom API in Admin → API Manager (\"Add Custom API\", id = firms_key).",
    storage: "n/a or system_api_keys (_id = firms_key) if you add it as custom.",
  },
  {
    section: "Free Public APIs (no key required)",
    id: "openaq",
    name: "OpenAQ",
    purpose: "Ground-station air-quality measurements (PM2.5, PM10, O3, NO2). Feeds Data Center AQI dataset.",
    used_in: ["/api/v1/air-quality", "Data Center AQI dataset"],
    test_endpoint: "https://api.openaq.org/v3/locations?limit=1",
    live_endpoint: "Same.",
    requires_key: false,
    free_tier: "Public, unlimited fair use.",
    signup: "n/a.",
    how_to_insert: "Nothing to insert.",
    storage: "n/a",
  },
  {
    section: "Free Public APIs (no key required)",
    id: "fx",
    name: "open.er-api.com (FX rates)",
    purpose: "Daily exchange rates with USD base. Drives the live FX conversion across donations, Carbon Hub purchases, and Developer plan subscriptions.",
    used_in: ["/api/fx/rates", "/api/fx/convert", "Frontend useFxRates hook"],
    test_endpoint: "https://open.er-api.com/v6/latest/USD",
    live_endpoint: "Same.",
    requires_key: false,
    free_tier: "Public free, ECB-derived daily rates. No key.",
    signup: "n/a.",
    how_to_insert: "Nothing to insert. Cached for 12h server-side.",
    storage: "in-memory cache",
  },
  {
    section: "Free Public APIs (no key required)",
    id: "nominatim",
    name: "OpenStreetMap Nominatim (geocoding)",
    purpose: "Search a place name and resolve to lat/lon. Reverse-geocode to a place label.",
    used_in: ["/api/geocode"],
    test_endpoint: "https://nominatim.openstreetmap.org/search?format=json&q=Berlin&limit=1",
    live_endpoint: "Same.",
    requires_key: false,
    free_tier: "1 req/sec fair-use. No key, but must send a unique User-Agent.",
    signup: "n/a.",
    how_to_insert: "Nothing to insert.",
    storage: "n/a",
  },
  {
    section: "Free Public APIs (no key required)",
    id: "gibs",
    name: "NASA GIBS WMTS (imagery)",
    purpose: "Daily MODIS / VIIRS / Suomi satellite tile imagery used by the dashboard map.",
    used_in: ["Dashboard Leaflet TileLayer, /api/nasa/snapshot, /api/timelapse/*"],
    test_endpoint: "https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/MODIS_Terra_NDVI/default/2024-01-01/GoogleMapsCompatible_Level9/0/0/0.png",
    live_endpoint: "Same.",
    requires_key: false,
    free_tier: "Public, no key.",
    signup: "n/a.",
    how_to_insert: "Nothing to insert.",
    storage: "n/a",
  },
  {
    section: "Free Public APIs (no key required)",
    id: "iss",
    name: "Open Notify / Where The ISS At",
    purpose: "Live position of the International Space Station for the dashboard space panel.",
    used_in: ["/api/space/iss", "/api/v1/space/iss"],
    test_endpoint: "https://api.wheretheiss.at/v1/satellites/25544",
    live_endpoint: "Same.",
    requires_key: false,
    free_tier: "Public.",
    signup: "n/a.",
    how_to_insert: "Nothing to insert.",
    storage: "n/a",
  },
];

// Convenience: section ordering
export const SECTIONS = [
  "NASA / Earth Observation",
  "Payments",
  "Transactional Email",
  "Weather & Maps (optional)",
  "Free Public APIs (no key required)",
];
